import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import useIsMounted from "../../../../hooks/use-is-mounted";
import Api from "../../../../../core/services/api/api";
import {Col, Row} from "reactstrap";
import SearchInput from "../../../../components/base/input/search_input";
import {routes} from "../../../../../core/models/static/routes/app-routes";
import {Tab, Tabs} from "../../../../components/base/Tabs";
import {numComparator} from "../../../../../core/services/utils/utils";
import {ReactComponent as NoColorIcon} from "../../../../../assets/images/notifications/no-color.svg";
import IconButton from "../../../../components/base/button/icon_button";
import {Paper} from "@mui/material";
import ColorPickerPopover from "../../../../components/app-specific/color-picker-popover";
import NotificationCategoryUpsertModal from "../../../../components/dialogs/notification-category-upsert";
import {ArrowBack, Edit} from "@mui/icons-material";
import TryAgain from "../../../../components/app-specific/try-again";
import {CSSTransition, TransitionGroup} from "react-transition-group";
import ReceivedNotificationCard from "../../../../components/app-specific/notification-cards/received-notification-card";
import InfiniteScroll from "react-infinite-scroller";
import classnames from "classnames";
import {useConfigureNotebook} from "../../../../hooks/use-notebook-config";
import {useHistory, useLocation} from "react-router-dom";
import useQuery from "../../../../hooks/use-query";
import useBehavioralAnalyticsTool from "../../../../hooks/use-behavioural-analytics-tool";
import useAccessLevels from "../../../../hooks/use-access-levels";


const initialPaginationInfo = {
    currentPage: 1,
    pageSize: 8,
    length: 0,
};

const allNotifications = {
    id: -2,
    orderIndex: -1,
    title: "All notifications"
}

export const defaultNotificationCategory = {
    id: -1,
    orderIndex: Number.MAX_SAFE_INTEGER - 1,
    title: "Default Saved Groups",
    colorCode: '#ffffff00',
}

const NotificationManagementViews = () => {
    const history = useHistory()
    const location = useLocation();
    const [query, setQuery] = useQuery()
    const [tabs, setTabs] = useState([])
    const [notifications, setNotifications] = useState([])
    const [loading, setLoading] = useState(true);
    const [fetching, setFetching] = useState(false);
    const [calledOnce, setCalledOnce] = useState(false);
    const [paginationInfo, setPaginationInfo] = useState(null)
    const [filters, setFilters] = useState({})
    const [categoryManagement, setCategoryManagement] = useState(false);
    const isMounted = useIsMounted()
    const {packages} = useAccessLevels();
    const canManageList = packages.notifications.manageList;
    const initialRender = useRef(true);
    const selectedTab = useMemo(() => Number(query.categoryId ?? allNotifications.id), [query.categoryId])
    const {configureNotebookTags} = useConfigureNotebook('notification-management-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool
     */
    useEffect(() => {
        configureNotebookTags((tags) => tags.notificationManagement);
        configureBehavioralTool((tags) => tags.notificationManagement);
    }, [configureNotebookTags, configureBehavioralTool]);

    /**
     * As soon as the component mounts:
     * Fetches the notification categories from the server.
     */
    useEffect(() => {
        getCategories();
    }, [])

    /**
     * Listens for the changes in the query of the url and with each change:
     * - if the tab or keywords of the query parameters have changed, then resets the pagination info, and sets the
     * filters to invoke the searching functionality.
     */
    useEffect(() => {
        let changed = false
        if (filters.categoryId !== Number(query?.categoryId)) {
            setFilters({categoryId: query.categoryId})
            changed = true
        }
        if (filters.keywords !== query?.keywords) {
            setFilters({keywords: query.keywords})
            changed = true
        }
        if (changed || paginationInfo === null) {
            resetState()
        }
    }, [query])


    /**
     * Listens for the changes in categoryManagement dialog state and with each change:
     *
     * if dialog is opened or its the first render of the component, then does nothing
     * if dialog is closed, then re-fetches the received notifications for the user.
     */
    useEffect(() => {
        if (initialRender.current) return;
        if (categoryManagement) {
            initialRender.current = false;
            return
        }
        resetState()
    }, [categoryManagement])


    /**
     * Listens to the changes in paginationInfo and with each change:
     * calls the search api for fetching the notifications.
     */
    useEffect(() => {
        if (!paginationInfo) return
        if (paginationInfo.currentPage === initialPaginationInfo.currentPage && calledOnce) return;
        const usingDefaultCategory = filters.categoryId === defaultNotificationCategory.id;
        const forApi = {
            paginationInfo: paginationInfo,
            filters: {
                isSearchWithCategory: filters.categoryId !== undefined,
                keyword: filters.keywords?.toString() ?? undefined,
                categoryId: usingDefaultCategory ? null : filters.categoryId,
            }
        }
        searchNotifications(forApi)
    }, [paginationInfo?.currentPage, calledOnce])

    /**
     * Handles the api errors when dealing with Biz-live system.
     *
     * in both cases, navigates the user to access denied view
     * @param {ApiResponseDs<null>} response
     */
    const unAuthorizedOrAccessDeniedCallback = (response) => {
        if (!isMounted()) return;
        switch (response?.errorCode) {
            case 401:
            case -401:
            case 403:
            case -403:
                history.push(location.pathname + routes.error.accessDeniedForSection)
                break;
            default:
                break;
        }
    }

    /**
     * Resets the paginationInfo and loading to invoke the search function.
     */
    const resetState = () => {
        setPaginationInfo(initialPaginationInfo)
        setNotifications([])
        setCalledOnce(false)
        setLoading(true)
    }


    /**
     * Sets the categories and tabs of the component.
     * @param {any[]} categories
     * @private
     */
    const _setCategories = (categories) => {
        const _categories = categories?.map(e => ({...e, tempColorCode: e.colorCode})) ?? []
        setTabs([
            allNotifications,
            ..._categories,
            defaultNotificationCategory,
        ])
    }

    /**
     * Fetches the notification categories of the system form the server and populates the state list.
     *
     * Also populates the list of available tabs of the view as well.
     * Note: navigates the user to access denied view in case of unauthorized or access denied responses.
     */
    const getCategories = () => {
        Api.getAllNotificationCategories(unAuthorizedOrAccessDeniedCallback).then(response => {
            if (!isMounted()) return
            if (response?.resultFlag) {
                _setCategories(response?.data ?? [])
            }
        })
    }


    /**
     * Searches among the received notification of the user given the search filters.
     *
     * Note: navigates the user to access denied view in case of unauthorized or access denied responses.
     * @param {any} forApi the data to be sent to the server.
     */
    const searchNotifications = (forApi) => {
        setFetching(true);
        Api.searchReceivedNotifications(forApi, unAuthorizedOrAccessDeniedCallback).then((response) => {
            if (!isMounted()) return
            if (response?.resultFlag) {
                setNotifications(prevState => {
                    const firstIndexToBeRemoved = (paginationInfo.currentPage - 1) * paginationInfo.pageSize;
                    return [
                        ...prevState.filter((e, i) => i < firstIndexToBeRemoved),
                        ...(response?.data?.resultList ?? [])
                    ]
                })
                setPaginationInfo(prevState => ({...prevState, length: response?.data?.paginationInfo?.length}))
            }
            setLoading(false)
            setCalledOnce(true)
            setFetching(false);
        })
    }

    /**
     * Loads more notifications by increasing the currentPage of pagination info to trigger the api call of searching.
     */
    const loadMore = useCallback(() => {
        if (fetching || loading) return;
        setFetching(true)
        setPaginationInfo(prevState => ({...prevState, currentPage: prevState.currentPage + 1}))
    }, [fetching, loading])

    const hasMore = useMemo(() => !loading && paginationInfo.length > paginationInfo.pageSize * paginationInfo.currentPage, [loading, paginationInfo])

    /**
     * Changes the selected categoryId of the query parameters to invoke the search behaviour.
     * @type {(event: any, newValue: any) => void}
     */
    const onTabChange = useCallback((_, newValue) => {
        setQuery(prevState => ({...prevState, categoryId: newValue === allNotifications.id ? undefined : newValue}))
    }, [setQuery]);

    /**
     * Changes the keywords of the query parameters to invoke the search behaviour.
     * @type {(string) => void}
     */
    const searchByKeywords = useCallback((keywords) => {
        setQuery(prevState => ({...prevState, keywords: !!keywords?.length ? keywords : undefined}))
    }, [setQuery])

    /**
     * Opens the category color picker dialog to change the category colour.
     * @param {MouseEvent} e
     * @param {function} onEnter
     */
    const onCategoryColorClicked = (e, onEnter) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.stopImmediatePropagation) {
            e.stopImmediatePropagation()
        }
        onEnter(e)
    }

    /**
     * Changes the color of this category in the server and then sets the inner state category.
     * @param {string} color
     * @param {any} category
     */
    const changeCategoryColor = (color, category) => {
        if (!canManageList) return;
        if (color === category.colorCode) return
        const forApi = {
            ...category,
            colorCode: color,
        }
        Api.updateNotificationCategory(forApi).then((response) => {
            if (!isMounted()) return;
            if (response?.resultFlag) {
                setTabs(prevState => prevState?.map(e => e.id === category.id ? forApi : e))
            } else {
                setTabs(prevState => prevState?.map(e => e.id === category.id ? {
                    ...e,
                    tempColorCode: e.colorCode
                } : e))
            }
        })
    }

    /**
     * SSets the temporary color of the selected category to reflect the changes in the ui immediately after the
     * user selection.
     * @param {string} color
     * @param {any} category
     */
    const changeTempColor = (color, category) => {
        setTabs(prevState => prevState?.map(e => e.id === category.id ? {...e, tempColorCode: color} : e))
    }


    /**
     * Opens the category management dialog.
     */
    const openCategoryManagement = () => {
        setCategoryManagement(true,);
    }

    /**
     * Closes the category management dialog and sets the new categories list
     * @param {boolean} open
     * @param {any[]} categories
     */
    const onCategoryManagementClosed = (open, categories = []) => {
        setCategoryManagement(open)
        _setCategories(categories ?? [])
        if (filters?.categoryId) {
            const found = categories?.find(e => e.id === parseInt(filters.categoryId))
            if (!found) {
                onTabChange(new Event('click'), allNotifications.id)
            }
        }
    }

    /**
     * Updates the internal state list by replacing the notification with a notification of the same id in the stet
     * list.
     *
     * if the selected tab is not allNotifications or keywords is used for searching, then sets the paginationInfo
     * in a way that the notification belonging to the pagination group that is loaded from the server is loaded
     * again.
     * @param {any} notification the notification
     */
    const updateNotificationCategory = (notification) => {
        if (selectedTab === allNotifications.id) {
            setNotifications(prevState => prevState.map(e => e.id === notification.id ? notification : e))
            return
        }
        const index = notifications?.findIndex(e => e.id === notification.id)
        if (index === -1) return;
        setCalledOnce(false);
        setPaginationInfo({
            ...initialPaginationInfo,
            currentPage: Math.floor(index / paginationInfo.pageSize) + 1,
        });
    }

    /**
     * Renders the loading components for this view.
     * @return {JSX.Element}
     */
    const renderLoadingComponents = () => (
        Array(6).fill(null).map((e, i) => (
            <div key={-(i + 1)} className={'loading received-notification-card w-100 mb-3'}>
                <div/>
            </div>
        ))
    );


    return (
        <>
            <Row className='no-gutters w-100 align-items-start justify-content-center notifications-view'>
                <Col xs={12}>
                    <p className={'text-lg font-weight-bold mt-4 mb-3'}>
                        Manage Received Notifications
                    </p>
                </Col>
                <Col xs={12}>
                    <p className={'text-sm text-color-2 mb-2'}>
                        Search among all received notifications.
                    </p>
                </Col>
                <Col xs={12}>
                    <SearchInput
                        initialValue={filters?.keywords ?? ''}
                        loading={loading}
                        autoSearch={false}
                        search={searchByKeywords}
                        placeholder={'search in all categories'}
                        label={'Search'}
                    />
                </Col>
            </Row>
            {
                !filters.keywords
                    ? <>
                        <Col xs={12} className='mb-3 notification-group-tabs'>
                            <div className={'edit-container'}>
                                <IconButton
                                    disabled={loading}
                                    onPressed={openCategoryManagement}
                                    color={'paper.secondary'}
                                >
                                    <Edit className='icon normal'/>
                                </IconButton>
                            </div>
                            <Tabs value={selectedTab} onChange={onTabChange}>
                                {
                                    tabs?.sort((a, b) => numComparator(a.orderIndex, b.orderIndex))
                                        ?.map((category, index) => (
                                            <Tab
                                                key={category.id}
                                                value={category.id}
                                                label={category.title}
                                                classes={{
                                                    root: classnames('negative-margin', {
                                                        'd-flex flex-row align-items-center justify-content-center': !(index === 0 || index === tabs.length - 1)
                                                    })
                                                }}
                                                icon={
                                                    !(index === 0 || index === tabs.length - 1) &&
                                                    <ColorPickerPopover
                                                        color={category.tempColorCode}
                                                        onImmediateChange={(c) => changeTempColor(c, category)}
                                                        onSelect={(c) => changeCategoryColor(c, category)}
                                                    >
                                                        {(onEnter) =>
                                                            <div className={'notification-group-circle'}
                                                                 onClick={(e) => onCategoryColorClicked(e, onEnter)}>

                                                                <NoColorIcon className={'placeholder'}/>
                                                                <div className={'circle'}
                                                                     style={{'--color': category.tempColorCode}}/>
                                                            </div>
                                                        }
                                                    </ColorPickerPopover>

                                                }
                                            />
                                        ))
                                }
                            </Tabs>
                        </Col>
                    </>
                    : <>
                        <Col xs={12} className='mb-3 mt-3 px-0 d-flex align-items-center'>
                            <IconButton
                                onPressed={() => searchByKeywords(undefined)}
                                color={'paper.secondary'}
                            >
                                <ArrowBack className={'icon normal'}/>
                            </IconButton>
                            <p className={'text-md font-weight-semi-bold black ml-2'}>
                                Back to categories
                            </p>
                        </Col>
                    </>
            }
            <Paper className='paper w-100 px-3 py-4 flex-wrap justify-content-between align-items-start'>
                {
                    loading
                        ? renderLoadingComponents()
                        : notifications?.length < 1
                            ? <TryAgain
                                text={`There is currently no notifications${
                                    !!filters?.keywords?.length
                                        ? ' with the specified keywords'
                                        : !!filters?.categoryId ? ' for the selected group' : ''
                                }.`}
                                onClick={resetState}
                            />
                            : <>
                                <InfiniteScroll
                                    pageStart={0}
                                    className={'w-100'}
                                    loadMore={loadMore}
                                    hasMore={hasMore}
                                    getScrollParent={() => document.querySelector('main')}
                                    useWindow={false}
                                >
                                    <TransitionGroup className={'d-flex flex-wrap w-100'}>
                                        {
                                            notifications?.map(notification => (
                                                <CSSTransition
                                                    key={notification.id}
                                                    classNames='item'
                                                    timeout={500}
                                                >
                                                    <ReceivedNotificationCard
                                                        categories={tabs?.filter(e => e.id !== allNotifications.id) ?? []}
                                                        notification={notification}
                                                        updateCategory={updateNotificationCategory}
                                                    />
                                                </CSSTransition>
                                            ))
                                        }
                                    </TransitionGroup>
                                </InfiniteScroll>
                                {fetching && renderLoadingComponents()}
                            </>
                }
            </Paper>
            <NotificationCategoryUpsertModal
                open={categoryManagement}
                setOpen={onCategoryManagementClosed}
            />
        </>
    )
}

export default NotificationManagementViews;
